"use client";

import { type DateValue, fromDate, getLocalTimeZone, parseDate } from "@internationalized/date";
import { useEffect, useRef, useState } from "react";
import { DialogTrigger, type Key } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { Calendar } from "../Calendar";
import { Icon } from "../Icon";
import { ListBoxItem } from "../ListBoxItem";
import { Popover } from "../Popover";
import { RangeCalendar } from "../RangeCalendar";
import { SegmentedControl } from "../SegmentedControl";
import { Select } from "../Select";

/**
 * Check if a Date object is valid (not Invalid Date)
 */
function isValidDate(date: Date): boolean {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

/**
 * Safely get a valid date, falling back to current time if invalid
 */
function safeDate(date: Date): Date {
  if (isValidDate(date)) {
    return date;
  }
  console.warn("Invalid date detected in TimeControls, using fallback:", date);
  return new Date();
}

/**
 * Window size for data aggregation
 * Matches GraphQL WindowSize enum, plus the telemetry-only rungs below it:
 * FIVE_MINUTES and ONE_MINUTE are `TelemetryBucketSize` capabilities that no
 * metrics query offers. ONE_MINUTE is served only by a power-quality sensor —
 * meters report at 5m or coarser — so a chart offering it must say so with its
 * own `windowSizeOptions` rather than relying on the defaults below.
 */
export type WindowSize = "ONE_MINUTE" | "FIVE_MINUTES" | "FIFTEEN_MINUTES" | "HOUR" | "DAY" | "WEEK" | "MONTH" | "YEAR";

/**
 * Date range with native Date objects
 */
export interface TimeRange {
  after: Date;
  before: Date;
}

/**
 * Preset range option
 */
export interface PresetRange {
  id: string;
  label: string;
  getRange: () => TimeRange;
}

/**
 * Window size option
 */
export interface WindowSizeOption {
  id: WindowSize;
  label: string;
}

/**
 * Props for TimeControls component
 */
export interface TimeControlsProps {
  /**
   * Current date range
   */
  dateRange: TimeRange;
  /**
   * Callback when date range changes. `meta.presetId` is set when the change
   * came from choosing a preset (vs. the calendar picker).
   */
  onDateRangeChange: (range: TimeRange, meta?: { presetId?: string }) => void;
  /**
   * Current window size (optional)
   */
  windowSize?: WindowSize;
  /**
   * Callback when window size changes (optional)
   */
  onWindowSizeChange?: (size: WindowSize) => void;
  /**
   * Whether to show window size selector
   * @default true
   */
  showWindowSize?: boolean;
  /**
   * Auto-adjust window size based on date range
   * @default false
   */
  autoAdjustWindowSize?: boolean;
  /**
   * Whether to show range selector presets
   * @default true
   */
  showRangeSelector?: boolean;
  /**
   * Whether to show calendar date picker button
   * @default false
   */
  allowDatePicker?: boolean;
  /**
   * Whether window size is locked (read-only)
   * @default false
   */
  lockWindowSize?: boolean;
  /**
   * Whether to show the built-in Reset button beside the calendar picker. It
   * returns to the first preset. Turn off in views that own their own reset
   * with different semantics (e.g. pan/zoom explorers returning to the last
   * explicit selection).
   * @default true
   */
  showReset?: boolean;
  /**
   * Restrict to single day selection (uses Calendar instead of RangeCalendar)
   * @default false
   */
  restrictToSingleDay?: boolean;
  /**
   * Available preset range options
   */
  presetRanges?: PresetRange[];
  /**
   * Available window size options
   */
  windowSizeOptions?: WindowSizeOption[];
  /**
   * Control size, matching the shared `Button` / `Select` scale.
   *
   * Defaults to `md`, which is what this component always rendered. `sm` exists
   * for a caller placing these beside smaller controls — a chart header, where
   * `md` here stood 39px next to 33px selects and read as a mismatch rather
   * than a hierarchy.
   */
  size?: "sm" | "md";
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * How far the range may sit from what a press produced and still count as that
 * press. Absorbs only the round trip — a consumer that clamps the range to its
 * own pan bounds hands back a value seconds off — so any deliberate pan clears
 * the highlight.
 */
const PRESS_ECHO_TOLERANCE_MS = 2 * 60 * 1000;

/**
 * How far a range may sit from a preset's *current* window and still be read as
 * that preset, as a fraction of the preset's own span. Scaled because a stale
 * hour means nothing to a 5-year window and everything to a 2-hour one.
 */
const PRESET_MATCH_TOLERANCE_FRACTION = 0.02;

const WINDOW_SIZE_SORT_MAP: Record<WindowSize, number> = {
  ONE_MINUTE: 0,
  FIVE_MINUTES: 1,
  FIFTEEN_MINUTES: 2,
  HOUR: 3,
  DAY: 4,
  WEEK: 5,
  MONTH: 6,
  YEAR: 7,
};

// Default window size options (excludes YEAR and MONTH)
const defaultWindowSizeOptions: WindowSizeOption[] = [
  { id: "FIFTEEN_MINUTES", label: "15m" },
  { id: "HOUR", label: "1h" },
  { id: "DAY", label: "1d" },
  { id: "WEEK", label: "1w" },
];

/**
 * TimeControls
 *
 * Sophisticated time control component with responsive behavior and calendar integration.
 * Features preset ranges, custom date picker, and window size selection.
 *
 * **Responsive Behavior:**
 * - < 600px: Compact mode with Select dropdowns
 * - ≥ 600px: Expanded mode with ToggleButton group for presets
 *
 * @example
 * ```tsx
 * const [dateRange, setDateRange] = useState({
 *   after: subDays(new Date(), 7),
 *   before: new Date()
 * });
 * const [windowSize, setWindowSize] = useState<WindowSize>("HOUR");
 *
 * <TimeControls
 *   dateRange={dateRange}
 *   onDateRangeChange={setDateRange}
 *   windowSize={windowSize}
 *   onWindowSizeChange={setWindowSize}
 *   presetRanges={presetRanges}
 *   allowDatePicker
 * />
 * ```
 */
export function TimeControls({
  dateRange,
  onDateRangeChange,
  windowSize,
  onWindowSizeChange,
  showWindowSize = true,
  showRangeSelector = true,
  allowDatePicker = false,
  lockWindowSize = false,
  showReset = true,
  restrictToSingleDay = false,
  presetRanges = [],
  windowSizeOptions = defaultWindowSizeOptions,
  autoAdjustWindowSize = false,
  size = "md",
  className,
}: TimeControlsProps) {
  const [componentWidth, setComponentWidth] = useState(0);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [hasManualWindowSize, setHasManualWindowSize] = useState(false);
  const [pressedPreset, setPressedPreset] = useState<{ id: string; after: number; before: number } | null>(null);

  // Auto-select appropriate window size based on date range duration
  const getAppropriateWindowSize = (range: TimeRange): WindowSize => {
    const durationMs = range.before.getTime() - range.after.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);

    if (durationHours <= 24) return "FIFTEEN_MINUTES"; // 1 day or less: 15min windows
    if (durationHours <= 72) return "HOUR"; // 3 days or less: 1h windows
    if (durationHours <= 168) return "HOUR"; // 7 days or less: 1h windows
    if (durationHours <= 720) return "DAY"; // 30 days or less: 1d windows
    return "DAY"; // More than 30 days: 1d windows
  };

  // Auto-adjust window size when date range changes (if enabled and not manually overridden)
  useEffect(() => {
    if (autoAdjustWindowSize && !hasManualWindowSize && onWindowSizeChange) {
      const appropriateSize = getAppropriateWindowSize(dateRange);
      onWindowSizeChange(appropriateSize);
    }
  }, [dateRange, autoAdjustWindowSize, hasManualWindowSize, onWindowSizeChange]);

  /**
   * Which preset a range looks like, for ranges this control did not produce —
   * first paint, or a parent driving the window itself.
   *
   * Takes the *closest* candidate rather than the first: a ladder whose entries
   * sit closer together than the tolerance (2h/6h/24h) has several qualify at
   * once, and taking the first lets a shorter preset shadow a longer one.
   */
  const findMatchingPresetRange = (range: TimeRange): string | undefined => {
    let best: { id: string; distance: number } | undefined;
    for (const presetRange of presetRanges) {
      const presetDates = presetRange.getRange();
      const tolerance = (presetDates.before.getTime() - presetDates.after.getTime()) * PRESET_MATCH_TOLERANCE_FRACTION;
      const afterDelta = Math.abs(presetDates.after.getTime() - range.after.getTime());
      const beforeDelta = Math.abs(presetDates.before.getTime() - range.before.getTime());
      if (afterDelta > tolerance || beforeDelta > tolerance) continue;
      const distance = afterDelta + beforeDelta;
      if (!best || distance < best.distance) best = { id: presetRange.id, distance };
    }
    return best?.id;
  };

  /**
   * A press records the exact window it produced, but `dateRange` stays the
   * authority: the id counts only while the prop still carries that window, so
   * a pan of any consequence clears the highlight while an untouched window
   * keeps it however far the preset's live `getRange()` anchor moves on.
   *
   * Neither half works alone. Stored state alone keeps highlighting a preset the
   * parent has panned away from; matching alone cannot tell a stale window from
   * a current one, because drift and panning are the same transformation.
   */
  const selectedPresetRange = (() => {
    if (
      pressedPreset &&
      presetRanges.some((preset) => preset.id === pressedPreset.id) &&
      Math.abs(pressedPreset.after - dateRange.after.getTime()) <= PRESS_ECHO_TOLERANCE_MS &&
      Math.abs(pressedPreset.before - dateRange.before.getTime()) <= PRESS_ECHO_TOLERANCE_MS
    ) {
      return pressedPreset.id;
    }
    return findMatchingPresetRange(dateRange);
  })();

  const safeDateAfter = safeDate(dateRange.after);
  const safeDateBefore = safeDate(dateRange.before);
  // `before` is an EXCLUSIVE bound; the label and the calendar both want the last instant inside the
  // window. Subtract 1ms, not 1 day: a day works out for whole-day ranges but inverts the label on
  // anything under 24h — a 4:00 AM–7:00 PM window on Aug 5 rendered as "8/5/2026 - 8/4/2026".
  // 1ms is right for both: Aug 1 00:00 → Aug 8 00:00 still reads 8/1 - 8/7.
  const endDate = new Date(safeDateBefore.getTime() - 1);

  const calendarValue = {
    start: fromDate(safeDateAfter, getLocalTimeZone()),
    end: fromDate(endDate, getLocalTimeZone()),
  };

  // ResizeObserver for responsive behavior
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setComponentWidth(entry.contentRect.width);
      }
    });

    if (toolbarRef.current) {
      resizeObserver.observe(toolbarRef.current);
    }

    return () => {
      if (toolbarRef.current) {
        resizeObserver.unobserve(toolbarRef.current);
      }
    };
  }, []);

  // Sort window size options
  const sortedWindowSizeOptions = [...windowSizeOptions].sort(
    (a, b) => WINDOW_SIZE_SORT_MAP[a.id] - WINDOW_SIZE_SORT_MAP[b.id]
  );

  const handlePresetChange = (value: string | Key) => {
    const presetId = value.toString();
    const preset = presetRanges.find((p) => p.id === presetId);
    if (preset) {
      // One getRange() call for both the record and the callback — a second call
      // re-anchors to a later `now` and would never match what the parent got.
      const range = preset.getRange();
      setPressedPreset({ id: presetId, after: range.after.getTime(), before: range.before.getTime() });
      setHasManualWindowSize(false); // Reset manual override when using preset
      onDateRangeChange(range, { presetId });
    }
  };

  const handleDatePickerChange = (value: { start: DateValue; end: DateValue } | null) => {
    if (value?.start && value?.end) {
      setPressedPreset(null);
      // Add +1 day to end date for inclusive end-of-day
      const startDate = new Date(value.start.year, value.start.month - 1, value.start.day, 0, 0, 0, 0);
      const endDate = new Date(value.end.year, value.end.month - 1, value.end.day + 1, 0, 0, 0, 0);
      onDateRangeChange({
        after: startDate,
        before: endDate,
      });
    }
  };

  const handleSingleDateChange = (value: DateValue | null) => {
    if (value) {
      setPressedPreset(null);
      const startDate = new Date(value.year, value.month - 1, value.day, 0, 0, 0, 0);
      const endDate = new Date(value.year, value.month - 1, value.day + 1, 0, 0, 0, 0);
      onDateRangeChange({
        after: startDate,
        before: endDate,
      });
    }
  };

  const handleWindowSizeChange = (value: Key) => {
    if (onWindowSizeChange) {
      setHasManualWindowSize(true); // Mark as manually set
      onWindowSizeChange(value.toString() as WindowSize);
    }
  };

  const handleReset = () => {
    if (presetRanges.length > 0) {
      handlePresetChange(presetRanges[0].id);
    }
  };

  const formatLocalDate = (date: Date) => safeDate(date).toLocaleDateString("en-US");

  // Convert options to SelectItem format for proper mobile tray label resolution
  const windowSizeItems = sortedWindowSizeOptions.map((option) => ({
    id: option.id,
    label: option.label,
    value: option.id as string,
  }));

  const presetRangeItems = presetRanges.map((preset) => ({
    id: preset.id,
    label: preset.label,
    value: preset.id,
  }));

  // Window size display (locked or selectable)
  const windowSizeControl = lockWindowSize ? (
    <div className="flex items-center rounded-md border border-border-muted bg-background-muted px-3 py-2 text-sm text-text-secondary">
      {sortedWindowSizeOptions.find((opt) => opt.id === windowSize)?.label || windowSize}
    </div>
  ) : (
    <Select
      className="w-full md:w-auto"
      selectedKey={windowSize}
      onSelectionChange={handleWindowSizeChange}
      items={windowSizeItems}
      size={size}
      aria-label="Select Window Size"
    >
      {(item) => (
        <ListBoxItem key={item.id} id={item.id} aria-label={item.label} size={size}>
          {item.label}
        </ListBoxItem>
      )}
    </Select>
  );

  const showsResetButton = showReset && !selectedPresetRange && presetRanges.length > 0;

  // Date picker button (single day or range)
  const datePicker = restrictToSingleDay ? (
    <div className="flex items-center">
      <DialogTrigger>
        <Button
          className="border-l pl-2 pr-2 hover:border-border-default hover:bg-background-muted hover:text-text-link-hover"
          variant="secondary"
          size={size}
        >
          <Icon name="CalendarBlank" size={18} />
          <span className="ml-2 hidden md:block">{formatLocalDate(dateRange.after)}</span>
        </Button>
        <Popover className="z-50 w-auto p-3" disableGroup>
          <Calendar
            aria-label="Select date"
            value={parseDate(safeDateAfter.toISOString().split("T")[0])}
            onChange={handleSingleDateChange}
          />
        </Popover>
      </DialogTrigger>
    </div>
  ) : (
    <div className="flex items-center">
      <DialogTrigger>
        <Button
          // Square right edge only while it butts against Reset.
          className={twMerge(
            "border-l pl-2 pr-2 hover:border-border-default hover:bg-background-muted hover:text-text-link-hover",
            showsResetButton && "rounded-r-none"
          )}
          variant="secondary"
          size={size}
        >
          <Icon name="CalendarBlank" size={18} />
          {!selectedPresetRange && (
            <span className="ml-2 hidden md:block">{`${formatLocalDate(dateRange.after)} - ${formatLocalDate(endDate)}`}</span>
          )}
        </Button>
        <Popover className="z-50 w-auto p-3" disableGroup>
          <RangeCalendar aria-label="Date range calendar" value={calendarValue} onChange={handleDatePickerChange} />
        </Popover>
      </DialogTrigger>
      {showsResetButton && (
        <Button variant="ghost" size={size} onPress={handleReset}>
          Reset
        </Button>
      )}
    </div>
  );

  // Responsive rendering
  const isCompact = componentWidth < 600;

  return (
    <div ref={toolbarRef} className={twMerge("flex w-full flex-wrap items-center justify-between gap-4", className)}>
      {isCompact ? (
        // Compact mode: Select dropdowns
        <div className="flex w-full justify-between">
          <div className="flex items-center">
            {allowDatePicker && datePicker}
            {showRangeSelector && presetRanges.length > 0 && (
              <Select
                selectedKey={selectedPresetRange ?? ""}
                onSelectionChange={handlePresetChange}
                items={presetRangeItems}
                size={size}
                aria-label="Select Date Range"
              >
                {(item) => (
                  <ListBoxItem key={item.id} id={item.id} size={size}>
                    {item.label}
                  </ListBoxItem>
                )}
              </Select>
            )}
          </div>
          <div className="flex items-center">{showWindowSize && windowSize && windowSizeControl}</div>
        </div>
      ) : (
        // Expanded mode: ToggleButton group
        <>
          <div className="flex items-center gap-4">
            {allowDatePicker && datePicker}
            {showRangeSelector && presetRanges.length > 0 && (
              <SegmentedControl
                options={presetRanges.map((preset) => ({
                  id: preset.id,
                  label: preset.label,
                }))}
                value={selectedPresetRange ?? ""}
                onChange={handlePresetChange}
                size="sm"
                variant="secondary"
                aria-label="Select Date Range"
              />
            )}
          </div>
          <div className="flex items-center">{showWindowSize && windowSize && windowSizeControl}</div>
        </>
      )}
    </div>
  );
}
